import { onScopeDispose } from "vue";
import type { QueryKey, QueryMatcher } from "./types";

export const DEFAULT_CACHE_TIME = 5 * 60 * 1000; // 5 minutes

export interface CacheEntry<TData = unknown> {
    key: QueryKey;
    data?: TData;
    updatedAt: number;
    promise?: Promise<TData>;
    cacheTime: number;
}

const serializeKey = (key: QueryKey): string => {
    return typeof key === "string" ? key : JSON.stringify(key);
};

const deserializeKey = (serialized: string): QueryKey => {
    try {
        const parsed = JSON.parse(serialized);
        return Array.isArray(parsed) ? (parsed as QueryKey) : serialized;
    } catch {
        return serialized;
    }
};

type InvalidationCallback = () => void;

export class QueryCache {
    private store = new Map<string, CacheEntry>();
    private invalidationListeners = new Map<string, Set<InvalidationCallback>>();

    get<TData>(key: QueryKey): CacheEntry<TData> | undefined {
        return this.store.get(serializeKey(key)) as CacheEntry<TData> | undefined;
    }

    subscribeToInvalidation(key: QueryKey, callback: InvalidationCallback): () => void {
        const serialized = serializeKey(key);
        if (!this.invalidationListeners.has(serialized)) {
            this.invalidationListeners.set(serialized, new Set());
        }
        this.invalidationListeners.get(serialized)!.add(callback);
        return () => {
            const listeners = this.invalidationListeners.get(serialized);
            if (listeners) {
                listeners.delete(callback);
                if (listeners.size === 0) {
                    this.invalidationListeners.delete(serialized);
                }
            }
        };
    }

    getBySerializedKey<TData>(serializedKey: string): CacheEntry<TData> | undefined {
        return this.store.get(serializedKey) as CacheEntry<TData> | undefined;
    }

    set<TData>(key: QueryKey, entry: Partial<CacheEntry<TData>>): CacheEntry<TData> {
        const serialized = serializeKey(key);
        const prev = this.store.get(serialized);
        const next: CacheEntry<TData> = {
            key,
            data: entry.data ?? (prev?.data as TData | undefined),
            updatedAt: entry.updatedAt ?? prev?.updatedAt ?? 0,
            promise: entry.promise ?? (prev?.promise as Promise<TData> | undefined),
            cacheTime: entry.cacheTime ?? prev?.cacheTime ?? DEFAULT_CACHE_TIME,
        };
        this.store.set(serialized, next);
        return next;
    }

    delete(key: QueryKey): void {
        this.store.delete(serializeKey(key));
    }

    invalidate(matcher: QueryMatcher): void {
        const invalidatedKeys = new Set<string>();

        if (typeof matcher === "function") {
            for (const [serialized, entry] of this.store.entries()) {
                if (matcher(entry.key)) {
                    this.store.delete(serialized);
                    invalidatedKeys.add(serialized);
                }
            }
        } else {
            const serialized = serializeKey(matcher);
            this.store.delete(serialized);
            invalidatedKeys.add(serialized);
        }

        // Уведомляем всех подписчиков об инвалидации
        for (const serialized of invalidatedKeys) {
            const listeners = this.invalidationListeners.get(serialized);
            if (listeners) {
                listeners.forEach(callback => callback());
            }
        }
    }

    clear(): void {
        this.store.clear();
    }

    cleanup(): void {
        const now = Date.now();
        for (const [serialized, entry] of this.store.entries()) {
            if (entry.cacheTime === Infinity) continue;
            if (now - entry.updatedAt > entry.cacheTime) {
                this.store.delete(serialized);
            }
        }
    }

    isFresh(entry: CacheEntry, staleTime: number): boolean {
        if (staleTime === Infinity) return true;
        return Date.now() - entry.updatedAt <= staleTime;
    }

    subscribeCleanup(): void {
        const interval = window.setInterval(() => {
            this.cleanup();
        }, DEFAULT_CACHE_TIME);

        onScopeDispose(() => {
            window.clearInterval(interval);
        });
    }
}

export const queryCache = new QueryCache();
export { serializeKey as serializeQueryKey, deserializeKey as deserializeQueryKey };

